import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as ClearFilter } from '../../../assets/icons/filter-close.svg';
import { ReactComponent as Filter } from '../../../assets/icons/filter.svg';
import { ReactComponent as ResetButton } from '../../../assets/icons/reset-button.svg';
// import { ReactComponent as SearchIcon } from '../../../assets/icons/search-icon.svg';
import { ReactComponent as LargeFontIcon } from '../../../assets/icons/table-component/large-font-icon.svg';
import { ReactComponent as MediumFontIcon } from '../../../assets/icons/table-component/medium-font-icon.svg';
import { ReactComponent as SmallFontIcon } from '../../../assets/icons/table-component/small-font-icon.svg';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/tables/expand-icon.svg';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { toggleFullScreen } from '../../../redux/slices/LayoutSlice';
import MyButton from '../../button/Button';
import CustomPopover from '../../custom-pop-over/CustomPopover';
import ExpandSearchBar from '../../expand-search-bar/ExpandSearchBar';
import ExportButton from '../../export-button/ExportButton';
import SwitchButton from '../../switch-button-field/SwitchButton';
import TableFieldAllow from '../../table-field-allow/TableFieldAllow';
import { TooltipIcon } from '../../tooltip/TooltipIcon';

import CustomTableFilterV2 from './TableFiltersV2';

interface CustomTableProperties {
  currentFontSize?: string;
  sizeChangeButtonRequired?: boolean;
  sizeChangeFunction?: () => void;
  rightTopContent?: JSX.Element;
  leftTopContent?: JSX.Element;
  filters?: CommonFilterProperties[];
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListLoading: boolean;
  filterListToDispatch?: AsyncThunk<any, void, any>;
  exportDataPdfOrCSV?: (type: 'pdf' | 'csv') => void;
  exportData?: any[];
  exportLoader?: boolean;
  isExportRequired?: boolean;
  labelForHideColumn?: {
    label: string;
    accessorKey: any;
    disableHideFunc: boolean;
  }[];
  hidedColumn?: string[];
  setHidedColumn?: React.Dispatch<React.SetStateAction<string[]>>;
  refershCurrentPages?: () => void;
  searchHoverWidth?: string;
  onClearFilterChange?: () => void;
  activeSearchFilter?: boolean;
  expandTableOpen?: boolean;
  expandTableOpenKey?: string;
  disableSearch?: boolean;
  requiredRefreshToggle?: boolean;
  refreshFunction?: () => any;
  refreshInterval?: number;
  autoRefreshEnabled?: boolean;
  disableAutoRefreshToggle?: boolean;
  refreshPosition?: 'left' | 'right';
  isAutoRefreshActive?: boolean;
  onAutoRefreshChange?: (isActive: boolean) => void;
  filterAllow?: boolean;
  onColumnOrderChange?: (columnOrder: string[]) => void;
  frozenColumn?: string | null;
  onFrozenColumnChange?: (columnKey: string | null) => void;
}

/**
 * Function to handle table filters
 * @param {any} properties Component properties
 * @returns {any} custom table header v2 component
 */
function CustomTableHeaderV2(properties: CustomTableProperties) {
  const { t, i18n } = useTranslation(['filters', 'sharedComponent', 'sidePanel']);
  const {
    currentFontSize,
    sizeChangeButtonRequired,
    rightTopContent,
    leftTopContent,
    sizeChangeFunction,
    filters,
    filterListLoading,
    filtersToDispatch,
    filterListToDispatch,
    exportDataPdfOrCSV,
    exportData,
    exportLoader,
    isExportRequired,
    labelForHideColumn,
    hidedColumn,
    setHidedColumn,
    refershCurrentPages,
    onClearFilterChange,
    expandTableOpen,
    expandTableOpenKey,
    disableSearch,
    requiredRefreshToggle,
    refreshInterval,
    refreshFunction,
    autoRefreshEnabled,
    disableAutoRefreshToggle,
    refreshPosition = 'right',
    isAutoRefreshActive,
    onAutoRefreshChange,
    filterAllow,
    onColumnOrderChange,
    frozenColumn,
    onFrozenColumnChange,
  } = properties;

  const dispatch = useAppDispatch();
  const fullScreenKey = useAppSelector((state) => state.layout.fullScreenKey);

  const isFullScreen = fullScreenKey === expandTableOpenKey;
  // As of now there should be only one search by text filter implemented
  const [searchFilter, setSearchFilter] = useState<CommonFilterProperties>();
  const [valueToShow, setValueToShow] = useState<string | number>('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [isAutoRefreshOn, setIsAutoRefreshOn] = useState<boolean>(
    isAutoRefreshActive !== undefined
      ? Boolean(isAutoRefreshActive && !disableAutoRefreshToggle)
      : Boolean(autoRefreshEnabled && !disableAutoRefreshToggle),
  );
  const intervalReference = useRef<NodeJS.Timeout | null>(null);

  /**
   * Translates a filter title or option label with casing and key fallbacks across loaded namespaces.
   * @param {string} [text] - Primary text or label (e.g. "Station Name", "Online").
   * @param {string} [secondaryKey] - Fallback key (e.g. "stationName", "online").
   * @returns {string} The localized translation or fallback text.
   */
  const translateFilterText = (text?: string, secondaryKey?: string): string => {
    if (!text && !secondaryKey) return '';

    const candidates: (string | undefined)[] = [
      text,
      secondaryKey,
      text ? text.charAt(0).toLowerCase() + text.slice(1) : undefined,
      secondaryKey ? secondaryKey.charAt(0).toLowerCase() + secondaryKey.slice(1) : undefined,
      text
        ? text
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
              index === 0 ? word.toLowerCase() : word.toUpperCase(),
            )
            .replace(/\s+/g, '')
        : undefined,
    ];

    const matchedCandidate = candidates.find((candidate) => {
      if (!candidate) return false;
      const translated = t(candidate, { defaultValue: '__MISSING__' });
      return Boolean(translated && translated !== '__MISSING__');
    });

    if (matchedCandidate) {
      return t(matchedCandidate);
    }

    return text || secondaryKey || '';
  };

  const hasCategoryFilters = Boolean(filters?.some((filter) => !filter.searchBy));
  const hasFilterListToFetch = Boolean(filterListToDispatch);
  const showFilterButton = filterAllow !== false && (hasCategoryFilters || hasFilterListToFetch);

  useEffect(() => {
    if (isAutoRefreshActive !== undefined) {
      setIsAutoRefreshOn(isAutoRefreshActive);
    }
  }, [isAutoRefreshActive]);

  useEffect(() => {
    if (!autoRefreshEnabled || disableAutoRefreshToggle) {
      setIsAutoRefreshOn(false);
    }
  }, [autoRefreshEnabled, disableAutoRefreshToggle]);
  /**
   * Function to count applied filters (excluding search filter)
   * @returns {any} number of applied filters
   */
  const countAppliedFilters = (): number => {
    if (!filters) return 0;

    return filters.filter((filter) => {
      // Skip search filter
      if (filter.searchBy) return false;

      // Count filters with applyFilter true
      if (filter.applyFilter) {
        // For date/time range filters
        if (filter.type === filterType.DATE_RANGE || filter.type === filterType.TIME_RANGE) {
          return true;
        }
        // For angle brackets filters
        if (filter.type === filterType.ANGLE_BRACKETS && filter?.value1 && filter?.value2) {
          return true;
        }
        // For multi-select filters
        if (Array.isArray(filter?.value) && filter.type === filterType.MULTISELECT) {
          return filter.value.length > 0;
        }
        // For array value filters
        if (Array.isArray(filter.value)) {
          return filter.value.length > 0;
        }
        // For string value filters
        if (typeof filter.value === 'string') {
          return filter.value !== '';
        }
      }
      return false;
    }).length;
  };

  /**
   * Function to decide font change to show
   * @returns {any} JSX.Element as an svg icon
   */
  const handleFontChangeIcon = (): JSX.Element => {
    let icon;
    switch (currentFontSize) {
      case 'large':
        icon = <LargeFontIcon />;
        break;
      case 'medium':
        icon = <MediumFontIcon />;
        break;
      default:
        icon = <SmallFontIcon />;
        break;
    }
    return icon;
  };

  /**
   * Function to remove filter from the list of selected filters
   * @param {any} key filter key to remove
   */
  const handleRemoveFilterByKey = (key: string) => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key,
          value: '', // Empty value as we are using same dispatch function for filter updates
          type: 'remove',
        }),
      );
    }
  };

  /**
   * Handle size change function
   */
  const handleChangeFilter = () => {
    if (!sizeChangeFunction) return;
    sizeChangeFunction();
  };

  /**
   * Function to clear all selected filters
   */
  const handleClearFilters = () => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: '', // Empty key as we are using same dispatch function for filter updates
          value: '', // Empty value as we are using same dispatch function for filter updates
          type: 'clear',
        }),
      );
    }
    if (onClearFilterChange) {
      onClearFilterChange();
    }
  };

  const refreshFunctionReference = useRef(refreshFunction);

  useEffect(() => {
    refreshFunctionReference.current = refreshFunction;
  }, [refreshFunction]);

  /**
   * Function to handle auto refresh
   */
  const handleAutoRefresh = () => {
    const next = !isAutoRefreshOn;
    setIsAutoRefreshOn(next);
    if (onAutoRefreshChange) {
      onAutoRefreshChange(next);
    }
    if (next && refreshFunctionReference.current) {
      refreshFunctionReference.current();
    }
  };

  useEffect(() => {
    if (isAutoRefreshOn) {
      const intervalMs = refreshInterval ? refreshInterval * 1000 : 10000;
      intervalReference.current = setInterval(() => {
        if (refreshFunctionReference.current) {
          refreshFunctionReference.current();
        }
      }, intervalMs);
    }

    return () => {
      if (intervalReference.current) {
        clearInterval(intervalReference.current);
      }
    };
  }, [isAutoRefreshOn, refreshInterval]);

  /**
   * Function to handle typing in the search filter
   * @param {any} name filter name
   * @param {any} value filter value
   */
  const handleChangeFilters = (name: string, value: string | number) => {
    setValueToShow(value);
  };

  useEffect(() => {
    if (filters) {
      const searchBy = filters.find((filter) => filter.searchBy);
      setSearchFilter(searchBy);
      setValueToShow(searchBy?.value.toString() || '');
    }
  }, [filters]);

  // Debounce functionality with 1s delay
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      const searchBy = filters?.find((filter) => filter.searchBy);
      const trimmedValue = valueToShow.toString().trim();
      if (filtersToDispatch && searchBy?.value.toString() !== trimmedValue) {
        dispatch(
          filtersToDispatch({
            key: 'searchBy',
            value: trimmedValue || '',
            type: 'add',
          }),
        );
      }
    }, 1000); // 1 second

    // Cleanup on unmount or valueToShow change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [valueToShow]);

  return (
    <div className="custom-table-header-wrap-v2">
      <div className="custom-table-header-first-row ">
        <div className="custom-table-left-wrap">
          <div className="custom-table-right-content">
            {/* <div className="custom-table-right-border" /> */}

            {showFilterButton && filters && filtersToDispatch && (
              <div className="custom-table-header-filter-wrap">
                <CustomPopover
                  clickableElement={
                    <>
                      <MyButton
                        label=""
                        buttonType="none"
                        onClickFunc={() => {}}
                        svgIcon={<Filter />}
                        hoverLabel={t('filter')}
                      />
                      <div className="tag tag-danger">{countAppliedFilters()}</div>
                    </>
                  }
                  popOverContent={
                    <CustomTableFilterV2
                      filters={filters}
                      filterListLoading={filterListLoading}
                      filtersToDispatch={filtersToDispatch}
                      filterListToDispatch={filterListToDispatch}
                    />
                  }
                />
              </div>
            )}
            {showFilterButton && filters && filtersToDispatch && (
              <div className="custom-table-filter-clear">
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={handleClearFilters}
                  svgIcon={<ClearFilter />}
                  hoverLabel={t('clearFilter')}
                />
              </div>
            )}
            {refershCurrentPages && refreshPosition === 'left' && (
              <div className="reset-button-data">
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={refershCurrentPages}
                  svgIcon={<ResetButton />}
                  hoverLabel={t('refresh')}
                />
              </div>
            )}
            {hidedColumn && setHidedColumn && labelForHideColumn && (
              <div className="hided-colum-data">
                <TableFieldAllow
                  labelForHideColumn={labelForHideColumn}
                  hidedColumn={hidedColumn}
                  setHidedColumn={setHidedColumn}
                  onColumnOrderChange={onColumnOrderChange}
                  frozenColumn={frozenColumn}
                  onFrozenColumnChange={onFrozenColumnChange}
                  hoverLabel={t('columnHide')}
                />
              </div>
            )}
            <div className="custom-table-size-change">
              {sizeChangeButtonRequired && (
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={handleChangeFilter}
                  svgIcon={handleFontChangeIcon()}
                  hoverLabel={t('textSize')}
                />
              )}
            </div>
            <div className="export-data-content">
              {exportDataPdfOrCSV && isExportRequired && (
                <div className="export-data">
                  <ExportButton
                    label=""
                    buttonList={[
                      {
                        key: 'pdf',
                        allow: true,
                        /**
                         * Export pdf function
                         */
                        onClickFunction: () => exportDataPdfOrCSV('pdf'),
                      },
                      {
                        key: 'csv',
                        allow: true,
                        /**
                         * Export csv function
                         */
                        onClickFunction: () => exportDataPdfOrCSV('csv'),
                      },
                    ]}
                    disabled={exportData?.length === 0}
                    loader={exportLoader}
                    version="v2"
                    hoverLabel={t('export')}
                  />
                </div>
              )}
            </div>
            {expandTableOpen && (
              <div>
                <MyButton
                  label=""
                  buttonType={isFullScreen ? 'primary-outline-none' : 'none'}
                  onClickFunc={() => dispatch(toggleFullScreen(expandTableOpenKey!))}
                  svgIcon={<ExpandIcon />}
                  hoverLabel={isFullScreen ? t('collapse') : t('fullScreen')}
                />
              </div>
            )}

            {searchFilter && !disableSearch ? (
              <div>
                <ExpandSearchBar
                  direction={i18n.dir() as 'ltr' | 'rtl'}
                  value={valueToShow}
                  onChange={(value) => handleChangeFilters(searchFilter.key, value)}
                  placeholder={translateFilterText(searchFilter.name, searchFilter.key)}
                />
              </div>
            ) : (
              <div className="search-select-content" />
            )}
            <div className="custom-table-right">
              {leftTopContent && (
                <div className="custom-table-left-container">{leftTopContent}</div>
              )}
            </div>
          </div>
        </div>
        <div className="custom-table-right">
          {requiredRefreshToggle && (
            <div className="auto-refresh-container">
              <span className={`label ${isAutoRefreshOn && 'active'}`}>
                {t('autoRefresh')}
                <TooltipIcon
                  text={t('autoRefreshDescription', {
                    seconds: refreshInterval ?? 10,
                  })}
                />
              </span>
              <SwitchButton
                checked={isAutoRefreshOn}
                change={disableAutoRefreshToggle ? () => {} : handleAutoRefresh}
                disabled={disableAutoRefreshToggle}
              />
            </div>
          )}
          {refershCurrentPages && refreshPosition === 'right' && (
            <div className="reset-button-data">
              <MyButton
                label=""
                buttonType="none"
                onClickFunc={refershCurrentPages}
                svgIcon={<ResetButton />}
                hoverLabel={t('refresh')}
              />
            </div>
          )}
          {rightTopContent && <div className="custom-table-right-container">{rightTopContent}</div>}
        </div>
      </div>
      {filters?.filter((filter) => !filter.searchBy).some((filter) => filter.applyFilter) && (
        <div className="custom-table-header-second-row">
          {filters
            .filter((filter) => !filter.searchBy)
            .map((filter) => {
              let filterTag = null;
              if (
                filter.applyFilter &&
                (filter.type === filterType.DATE_RANGE || filter.type === filterType.TIME_RANGE)
              ) {
                filterTag = (
                  <div className={`filter-tag-wrap ${filter.type && 'dateRange'}`}>
                    {' '}
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details-date">
                      <p>{translateFilterText(filter.name, filter.key)}: </p>
                      <p>{filter.value1}</p>
                      <p>-</p>
                      <p>{filter.value2}</p>
                    </div>
                    {filter?.disableClearFilter ? (
                      <span
                        className="close-icon disable"
                        title={filter?.disableClearFilterMessage || t('somethingWentWrong')}>
                        <CloseIcon />
                      </span>
                    ) : (
                      <span
                        className="close-icon"
                        role="presentation"
                        onClick={() => handleRemoveFilterByKey(filter.key)}>
                        <CloseIcon />
                      </span>
                    )}
                  </div>
                );
              } else if (
                filter.applyFilter &&
                filter.type === filterType.ANGLE_BRACKETS &&
                filter?.value1 &&
                filter?.value2
              ) {
                filterTag = (
                  <div className={`filter-tag-wrap ${filter.type && 'angleBrackets'}`}>
                    {' '}
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details-date">
                      <p>{translateFilterText(filter.name, filter.key)}: </p>
                      <p>{filter.value2}</p>
                      <p>{filter.value1}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (
                filter.applyFilter &&
                Array.isArray(filter?.value) &&
                filter.type === filterType.MULTISELECT
              ) {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value.join(', ')}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{translateFilterText(filter.name, filter.key)}:</h6>
                      {filter?.valueToShow ? (
                        <p>
                          {filter?.valueToShow
                            .map((value) => translateFilterText(value))
                            .join(', ')}
                        </p>
                      ) : (
                        <p />
                      )}
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (filter.applyFilter && Array.isArray(filter.value)) {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value.join(', ')}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{translateFilterText(filter.name, filter.key)}: </h6>
                      <p>
                        {filter.value
                          .map((value) =>
                            translateFilterText(
                              filter.listOfValue?.find((item) => item.value === value)?.label ||
                                value,
                              value,
                            ),
                          )
                          .join(', ')}
                      </p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (filter.applyFilter && typeof filter.value === 'string') {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{translateFilterText(filter.name, filter.key)}: </h6>
                      <p>
                        {translateFilterText(
                          filter.listOfValue?.find((item) => item.value === filter.value)?.label ||
                            filter.value,
                          filter.value,
                        )}
                      </p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              }
              return filterTag;
            })}
        </div>
      )}
    </div>
  );
}

export default CustomTableHeaderV2;
